import { useMemo } from 'react'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'

type LoadSource = 'sidebar' | 'commandK'

// `multiple` covers the case where a single mutation touches more than one
// of {name, filters, view, icon}; analysts segment on rename vs filters vs
// view vs icon (the view's identity: icon + color).
type UpdateType = 'rename' | 'filters' | 'view' | 'icon' | 'multiple'

// Minimal shape needed to emit events — kept structural so both
// SavedViewsSection (full SavedView from gql) and Command-K (whatever
// payload it carries) can satisfy it without coupling this hook to a
// component-local GraphQL fragment.
type SavedViewIdentity = {
  id: string
  section: string
  view: string | null
}

type SavedViewTracking = {
  trackCreated: (view: SavedViewIdentity) => void
  trackLoaded: (view: SavedViewIdentity, source: LoadSource) => void
  trackUpdated: (view: SavedViewIdentity, updateType?: UpdateType) => void
  trackDeleted: (view: SavedViewIdentity) => void
  trackReordered: (viewIds: readonly string[]) => void
}

const NOOP: SavedViewTracking = {
  trackCreated: () => {},
  trackLoaded: () => {},
  trackUpdated: () => {},
  trackDeleted: () => {},
  trackReordered: () => {},
}

/**
 * Single entry point for the five Saved Views Segment events (CORE-4672).
 * Centralises the common properties (organizationId, clientName) so consumers
 * only pass the view-specific payload. Returns a no-op tracker when no
 * organization is available, so callers don't need their own guards.
 */
export function useSavedViewTracking(): SavedViewTracking {
  const organizationId = useCurrentOrganization()?.id

  return useMemo(() => {
    if (!organizationId) return NOOP

    const baseProps = (
      view: SavedViewIdentity,
    ): Parameters<typeof BufferTracker.savedViewCreated>[0] => ({
      organizationId,
      clientName: 'publishWeb',
      viewId: view.id,
      section: view.section,
      view: view.view,
    })

    return {
      trackCreated: (view) => {
        BufferTracker.savedViewCreated(baseProps(view))
      },
      trackLoaded: (view, source) => {
        BufferTracker.savedViewLoaded({ ...baseProps(view), source })
      },
      trackUpdated: (view, updateType) => {
        BufferTracker.savedViewUpdated({
          ...baseProps(view),
          ...(updateType ? { updateType } : {}),
        })
      },
      trackDeleted: (view) => {
        BufferTracker.savedViewDeleted(baseProps(view))
      },
      trackReordered: (viewIds) => {
        BufferTracker.savedViewReordered({
          organizationId,
          clientName: 'publishWeb',
          viewIds: [...viewIds],
          viewCount: viewIds.length,
        })
      },
    }
  }, [organizationId])
}
